// src/pages/admin/DashboardHome.jsx
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import {
  FiGrid,
  FiUsers,
  FiMessageSquare,
  FiTrendingUp,
  FiSettings,
  FiPlus,
  FiMail,
  FiBriefcase,
  FiClock,
  FiDollarSign,
  FiFlag,
  FiActivity,
  FiPieChart,
  FiRefreshCw,
  FiArrowRight,
  FiTarget,
  FiZap,
  FiUser,
  FiPackage,
  FiCalendar,
  FiEye,
  FiStar,
  FiBarChart2,
  FiPieChart as FiPieChartIcon,
  FiTrendingDown,
  FiAlertCircle,
  FiCheckCircle,
  FiXCircle,
  FiLoader,
  FiDownload,
  FiFilter,
  FiShoppingBag,
  FiHeart,
  FiAward,
  FiGlobe,
  FiThumbsUp,
  FiTool,
  FiBookOpen,
  FiTag,
  FiFolder,
} from 'react-icons/fi';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePortfolio } from '../../hooks/usePortfolio';
import {
  db,
  collection,
  query,
  orderBy,
  onSnapshot,
  where,
  getDocs,
  getCountFromServer,
  doc,
  getDoc,
} from '../../services/firebase';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Bar, Doughnut, Pie } from 'react-chartjs-2';
import { NavLink, useNavigate } from 'react-router-dom';
import reviewService from '../../services/reviewService';
import StarRating from '../../components/StarRating';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
  Filler,
);

// =============================================
// ANIMATED COUNTER COMPONENT
// =============================================
const AnimatedCounter = React.memo(
  ({ value, suffix = '', duration = 1000 }) => {
    const [displayValue, setDisplayValue] = useState(0);
    const countRef = useRef(null);
    const hasAnimated = useRef(false);

    useEffect(() => {
      const observer = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting && !hasAnimated.current) {
              hasAnimated.current = true;
              let startTime;
              let animationFrame;
              const numValue =
                typeof value === 'number' ? value : parseFloat(value) || 0;
              const updateCount = timestamp => {
                if (!startTime) startTime = timestamp;
                const progress = Math.min(
                  (timestamp - startTime) / duration,
                  1,
                );
                setDisplayValue(Math.floor(progress * numValue));
                if (progress < 1) {
                  animationFrame = requestAnimationFrame(updateCount);
                }
              };
              animationFrame = requestAnimationFrame(updateCount);
              return () => cancelAnimationFrame(animationFrame);
            }
          });
        },
        { threshold: 0.3 },
      );

      if (countRef.current) {
        observer.observe(countRef.current);
      }
      return () => observer.disconnect();
    }, [value, duration]);

    const formatValue = () => {
      if (suffix === '%') {
        return `${displayValue}%`;
      }
      if (suffix === 'k' && displayValue >= 1000) {
        return `${(displayValue / 1000).toFixed(1)}k`;
      }
      return displayValue.toLocaleString();
    };

    return (
      <span
        ref={countRef}
        className="font-bold"
        style={{ color: 'var(--text)' }}
      >
        {formatValue()}
      </span>
    );
  },
);

AnimatedCounter.displayName = 'AnimatedCounter';

// =============================================
// STATS CARD COMPONENT
// =============================================
const StatsCard = React.memo(
  ({ title, value, icon: Icon, onClick, trend, trendValue, subtitle }) => {
    const [isHovered, setIsHovered] = useState(false);
    const { actualTheme } = useTheme();
    const isDark = actualTheme === 'dark';

    return (
      <div
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={onClick}
        className="rounded-xl p-3 border transition-all duration-300 hover:scale-[1.02] cursor-pointer group"
        style={{
          backgroundColor: 'var(--bg)',
          borderColor: 'var(--border)',
        }}
      >
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <p
              className="text-[11px] font-medium uppercase tracking-wide"
              style={{ color: 'var(--text)' }}
            >
              {title}
            </p>
            <div
              className="text-xl md:text-2xl font-bold mt-1"
              style={{ color: 'var(--text)' }}
            >
              {typeof value === 'number' ? (
                <AnimatedCounter value={value} />
              ) : (
                value
              )}
            </div>
            {subtitle && (
              <p
                className="text-[10px] mt-0.5"
                style={{ color: 'var(--text)' }}
              >
                {subtitle}
              </p>
            )}
            {trend && (
              <div className="flex items-center gap-1 mt-1">
                {trend === 'up' ? (
                  <FiTrendingUp
                    className="w-2.5 h-2.5"
                    style={{ color: '#10b981' }}
                  />
                ) : trend === 'down' ? (
                  <FiTrendingDown
                    className="w-2.5 h-2.5"
                    style={{ color: '#ef4444' }}
                  />
                ) : null}
                <span
                  className={`text-[10px] ${trend === 'up' ? 'text-green-500' : trend === 'down' ? 'text-red-500' : ''}`}
                  style={{
                    color:
                      trend === 'up'
                        ? '#10b981'
                        : trend === 'down'
                          ? '#ef4444'
                          : 'var(--text)',
                  }}
                >
                  {trendValue}
                </span>
              </div>
            )}
          </div>
          <div
            className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-300 ${
              isHovered ? 'scale-110 rotate-6' : ''
            }`}
            style={{
              backgroundColor: isDark
                ? 'rgba(255,255,255,0.1)'
                : 'rgba(0,0,0,0.05)',
            }}
          >
            <Icon className="w-4 h-4" style={{ color: 'var(--text)' }} />
          </div>
        </div>
      </div>
    );
  },
);

StatsCard.displayName = 'StatsCard';

const chartColors = {
  primary: '#8b5cf6',
  secondary: '#ec4899',
  success: '#10b981',
  warning: '#f59e0b',
  error: '#ef4444',
  info: '#3b82f6',
  cyan: '#06b6d4',
  orange: '#f97316',
  purple: '#a855f7',
  emerald: '#059669',
};

// =============================================
// MAIN DASHBOARD COMPONENT
// =============================================
function DashboardHome() {
  const { actualTheme } = useTheme();
  const { currentUser, getAuthToken } = useAuth();
  const {
    projects,
    loading: portfolioLoading,
    refresh: refreshPortfolio,
  } = usePortfolio();
  const isDark = actualTheme === 'dark';
  const navigate = useNavigate();
  const isInitializedRef = useRef(false);
  const subscriptionsRef = useRef([]);

  // State
  const [dashboardData, setDashboardData] = useState({
    portfolio: {
      total: 0,
      byCategory: { branding: 0, product: 0, others: 0 },
      featured: 0,
      recentProjects: [],
    },
    contacts: {
      total: 0,
      unread: 0,
      read: 0,
      spam: 0,
      archived: 0,
      responseRate: 0,
      monthlyData: [],
      recentMessages: [],
    },
    hires: {
      total: 0,
      pending: 0,
      inReview: 0,
      responded: 0,
      approved: 0,
      declined: 0,
      completed: 0,
      archived: 0,
      conversionRate: 0,
      budgetDistribution: {},
      monthlyData: [],
      recentInquiries: [],
    },
    readyMadeServices: {
      total: 0,
      active: 0,
      draft: 0,
      popular: 0,
      monthlyGrowth: [],
    },
    readyMadeInquiries: {
      total: 0,
      pending: 0,
      responded: 0,
      approved: 0,
      declined: 0,
      monthlyData: [],
    },
    subscriptionPlans: { total: 0, active: 0, draft: 0, popular: 0 },
    subscriptionInquiries: {
      total: 0,
      pending: 0,
      responded: 0,
      approved: 0,
      declined: 0,
      monthly: 0,
      yearly: 0,
      monthlyData: [],
    },
    reviews: {
      total: 0,
      approved: 0,
      pending: 0,
      rejected: 0,
      averageRating: 0,
      ratingDistribution: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 },
      recentReviews: [],
      monthlyData: [],
    },
    blog: {
      total: 0,
      published: 0,
      draft: 0,
      scheduled: 0,
      archived: 0,
      totalViews: 0,
      totalLikes: 0,
      totalComments: 0,
      totalReactions: 0,
      avgReadingTime: 0,
      engagementRate: 0,
      categoryDistribution: {},
      topPerforming: [],
      recentPosts: [],
      commentsOverTime: [],
      monthlyData: [],
    },
    blogComments: {
      total: 0,
      totalReactions: 0,
      avgReactionsPerComment: 0,
      activeCommenters: 0,
      reactionDistribution: { like: 0, love: 0, haha: 0, sad: 0, angry: 0 },
      topCommenters: [],
      topCommentedBlogs: [],
      hourlyDistribution: [],
      dailyActivity: [],
      monthlyData: [],
    },
    loading: true,
    lastUpdated: new Date(),
  });

  const [refreshing, setRefreshing] = useState(false);

  // ==================== REVIEWS FUNCTIONS ====================

  // ==================== REVIEWS FUNCTIONS - FIXED ====================

  const fetchReviewsData = useCallback(async () => {
    try {
      const reviewStats = await reviewService.getStats();

      // FIX: getAllReviews returns an object with { reviews, lastVisible, hasMore }
      const result = await reviewService.getAllReviews();

      // Extract the reviews array from the result
      const allReviews = result.reviews || [];

      // Ensure all reviews have valid dates
      const validatedReviews = allReviews.map(review => ({
        ...review,
        createdAt:
          review.createdAt && !isNaN(new Date(review.createdAt).getTime())
            ? review.createdAt
            : new Date().toISOString(),
      }));

      const recentReviews = validatedReviews
        .filter(r => r.status === 'approved')
        .sort((a, b) => {
          const dateA = new Date(a.createdAt || 0);
          const dateB = new Date(b.createdAt || 0);
          return dateB - dateA;
        })
        .slice(0, 5);

      const monthlyMap = new Map();
      validatedReviews
        .filter(r => r.status === 'approved')
        .forEach(review => {
          try {
            const createdAt = review.createdAt
              ? new Date(review.createdAt)
              : new Date();
            if (isNaN(createdAt.getTime())) return;
            const monthKey = `${createdAt.getFullYear()}-${String(createdAt.getMonth() + 1).padStart(2, '0')}`;
            monthlyMap.set(monthKey, (monthlyMap.get(monthKey) || 0) + 1);
          } catch (err) {
            // Skip invalid dates
          }
        });

      const monthlyData = Array.from(monthlyMap.entries())
        .sort((a, b) => a[0].localeCompare(b[0]))
        .slice(-6)
        .map(([month, count]) => ({ month: month.slice(5), count }));

      setDashboardData(prev => ({
        ...prev,
        reviews: {
          total: reviewStats.total || 0,
          approved: reviewStats.approved || 0,
          pending: reviewStats.pending || 0,
          rejected: reviewStats.rejected || 0,
          averageRating: reviewStats.averageRating || 0,
          ratingDistribution: reviewStats.ratingDistribution || {
            5: 0,
            4: 0,
            3: 0,
            2: 0,
            1: 0,
          },
          recentReviews,
          monthlyData,
        },
      }));
    } catch (error) {
      console.error('Error fetching reviews data:', error);
      // Set default values on error
      setDashboardData(prev => ({
        ...prev,
        reviews: {
          total: 0,
          approved: 0,
          pending: 0,
          rejected: 0,
          averageRating: 0,
          ratingDistribution: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 },
          recentReviews: [],
          monthlyData: [],
        },
      }));
    }
  }, []);
  // ==================== BLOG ANALYTICS FUNCTIONS ====================

  const fetchBlogAnalytics = useCallback(async () => {
    try {
      const token = await getAuthToken();
      if (!token) return;

      const response = await fetch(
        `/api/blogs?action=analytics-overview&period=30days`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const data = await response.json();

      if (data.success) {
        const overview = data.overview || {};
        const distribution = data.distribution || {};
        const performance = data.performance || {};
        const engagement = data.engagement || {};

        // Calculate monthly data for blog posts
        const monthlyMap = new Map();
        try {
          const blogsSnapshot = await getDocs(collection(db, 'blogs'));
          blogsSnapshot.forEach(doc => {
            const blog = doc.data();
            if (blog.status === 'published' && blog.publishedAt) {
              const date = new Date(blog.publishedAt);
              const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
              monthlyMap.set(monthKey, (monthlyMap.get(monthKey) || 0) + 1);
            }
          });
        } catch (e) {
          console.error('Error fetching blog monthly data:', e);
        }

        const monthlyData = Array.from(monthlyMap.entries())
          .sort((a, b) => a[0].localeCompare(b[0]))
          .slice(-6)
          .map(([month, count]) => ({ month: month.slice(5), count }));

        // Get recent blog posts
        let recentPosts = [];
        try {
          const recentSnapshot = await getDocs(
            query(
              collection(db, 'blogs'),
              orderBy('createdAt', 'desc'),
              where('status', '==', 'published'),
            ),
          );
          const posts = [];
          recentSnapshot.forEach(doc => {
            const blog = doc.data();
            posts.push({
              id: doc.id,
              title: blog.title,
              slug: blog.slug,
              views: blog.views || 0,
              category: blog.category,
              readingTime: blog.readingTime || 0,
              publishedAt: blog.publishedAt,
            });
          });
          recentPosts = posts.slice(0, 5);
        } catch (e) {
          console.error('Error fetching recent posts:', e);
        }

        // Get blog comment analytics
        let blogCommentData = {
          total: 0,
          totalReactions: 0,
          avgReactionsPerComment: 0,
          activeCommenters: 0,
          reactionDistribution: { like: 0, love: 0, haha: 0, sad: 0, angry: 0 },
          topCommenters: [],
          topCommentedBlogs: [],
          hourlyDistribution: Array(24).fill(0),
          dailyActivity: [],
          monthlyData: [],
        };

        try {
          const commentsSnapshot = await getDocs(
            query(
              collection(db, 'blogComments'),
              where('isApproved', '==', true),
            ),
          );
          const comments = [];
          commentsSnapshot.forEach(doc =>
            comments.push({ id: doc.id, ...doc.data() }),
          );

          blogCommentData.total = comments.length;

          // Calculate reactions
          const reactionDist = { like: 0, love: 0, haha: 0, sad: 0, angry: 0 };
          let totalReactions = 0;
          comments.forEach(c => {
            const reactions = c.reactions || {};
            Object.entries(reactions).forEach(([key, count]) => {
              if (reactionDist[key] !== undefined) {
                reactionDist[key] += count;
                totalReactions += count;
              }
            });
          });
          blogCommentData.totalReactions = totalReactions;
          blogCommentData.reactionDistribution = reactionDist;
          blogCommentData.avgReactionsPerComment =
            comments.length > 0
              ? (totalReactions / comments.length).toFixed(1)
              : 0;

          // Top commenters
          const commenters = {};
          comments.forEach(c => {
            const email = c.authorEmail;
            if (!commenters[email]) {
              commenters[email] = {
                name: c.authorName,
                email: c.authorEmail,
                count: 0,
              };
            }
            commenters[email].count++;
          });
          blogCommentData.topCommenters = Object.values(commenters)
            .sort((a, b) => b.count - a.count)
            .slice(0, 5);

          // Top commented blogs
          const blogCommentCounts = {};
          comments.forEach(c => {
            blogCommentCounts[c.blogId] =
              (blogCommentCounts[c.blogId] || 0) + 1;
          });
          const topBlogs = Object.entries(blogCommentCounts)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 5);

          // Get blog titles
          const blogTitles = {};
          for (const [blogId] of topBlogs) {
            try {
              const blogDoc = await getDoc(doc(db, 'blogs', blogId));
              if (blogDoc.exists()) {
                blogTitles[blogId] = blogDoc.data().title;
              }
            } catch (e) {}
          }

          blogCommentData.topCommentedBlogs = topBlogs.map(
            ([blogId, count]) => ({
              blogId,
              title: blogTitles[blogId] || 'Unknown Blog',
              count,
            }),
          );

          // Hourly distribution
          comments.forEach(c => {
            const hour = new Date(c.createdAt).getHours();
            blogCommentData.hourlyDistribution[hour]++;
          });

          // Monthly comment data
          const commentMonthlyMap = new Map();
          comments.forEach(c => {
            const date = new Date(c.createdAt);
            const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            commentMonthlyMap.set(
              monthKey,
              (commentMonthlyMap.get(monthKey) || 0) + 1,
            );
          });
          blogCommentData.monthlyData = Array.from(commentMonthlyMap.entries())
            .sort((a, b) => a[0].localeCompare(b[0]))
            .slice(-6)
            .map(([month, count]) => ({ month: month.slice(5), count }));

          blogCommentData.activeCommenters = Object.keys(commenters).length;
        } catch (e) {
          console.error('Error fetching blog comment analytics:', e);
        }

        // Ensure category distribution is properly formatted
        const categoryDist = distribution.categories || [];
        const categoryDistribution = {};
        if (Array.isArray(categoryDist)) {
          categoryDist.forEach(cat => {
            if (cat && cat.name) {
              categoryDistribution[cat.name] = cat.count || 0;
            }
          });
        }

        setDashboardData(prev => ({
          ...prev,
          blog: {
            total: overview.totalBlogs || 0,
            published: overview.publishedBlogs || 0,
            draft: overview.draftBlogs || 0,
            scheduled: overview.scheduledBlogs || 0,
            archived: overview.archivedBlogs || 0,
            totalViews: overview.totalViews || 0,
            totalLikes: overview.totalLikes || 0,
            totalComments: overview.totalComments || 0,
            totalReactions: overview.totalReactions || 0,
            avgReadingTime: overview.avgReadingTime || 0,
            engagementRate: parseFloat(overview.engagementRate) || 0,
            categoryDistribution: categoryDistribution,
            topPerforming: performance.topPerforming || [],
            recentPosts: recentPosts || [],
            commentsOverTime: engagement.commentsOverTime || [],
            monthlyData: monthlyData || [],
          },
          blogComments: blogCommentData,
        }));
      }
    } catch (error) {
      console.error('Error fetching blog analytics:', error);
    }
  }, [getAuthToken]);

  // ==================== FETCH FUNCTIONS ====================

  const fetchReadyMadeServicesStats = useCallback(async () => {
    try {
      const snapshot = await getDocs(collection(db, 'readyMadeServices'));
      let active = 0,
        draft = 0,
        popular = 0;
      const monthlyMap = new Map();

      snapshot.forEach(doc => {
        const data = doc.data();
        if (data.status === 'active') active++;
        if (data.status === 'draft') draft++;
        if (data.popular) popular++;

        const createdAt =
          data.createdAt?.toDate?.() || new Date(data.createdAt);
        const monthKey = `${createdAt.getFullYear()}-${String(createdAt.getMonth() + 1).padStart(2, '0')}`;
        monthlyMap.set(monthKey, (monthlyMap.get(monthKey) || 0) + 1);
      });

      const monthlyGrowth = Array.from(monthlyMap.entries())
        .sort((a, b) => a[0].localeCompare(b[0]))
        .slice(-6)
        .map(([month, count]) => ({ month: month.slice(5), count }));

      setDashboardData(prev => ({
        ...prev,
        readyMadeServices: {
          total: snapshot.size,
          active,
          draft,
          popular,
          monthlyGrowth,
        },
      }));
    } catch (error) {
      console.error('Error fetching ready-made services:', error);
    }
  }, []);

  const fetchSubscriptionPlansStats = useCallback(async () => {
    try {
      const snapshot = await getDocs(collection(db, 'subscriptionPlans'));
      let active = 0,
        draft = 0,
        popular = 0;
      snapshot.forEach(doc => {
        const data = doc.data();
        if (data.status === 'active') active++;
        if (data.status === 'draft') draft++;
        if (data.popular) popular++;
      });
      setDashboardData(prev => ({
        ...prev,
        subscriptionPlans: { total: snapshot.size, active, draft, popular },
      }));
    } catch (error) {
      console.error('Error fetching subscription plans:', error);
    }
  }, []);

  // ==================== REAL-TIME SUBSCRIPTIONS ====================

  const processContactData = useCallback(snapshot => {
    const monthlyMap = new Map();
    const recentMessages = [];
    let unread = 0,
      read = 0,
      spam = 0,
      archived = 0;

    snapshot.forEach(doc => {
      const data = doc.data();
      const createdAt = data.createdAt?.toDate?.() || new Date(data.createdAt);
      const monthKey = `${createdAt.getFullYear()}-${String(createdAt.getMonth() + 1).padStart(2, '0')}`;
      monthlyMap.set(monthKey, (monthlyMap.get(monthKey) || 0) + 1);

      if (data.isSpam) spam++;
      else if (data.isArchived) archived++;
      else if (data.status === 'unread') unread++;
      else if (data.status === 'read') read++;

      recentMessages.push({
        id: doc.id,
        name: data.name || 'Anonymous',
        message: data.message?.substring(0, 60) || '',
        status: data.status || 'unknown',
        createdAt,
      });
    });

    const monthlyData = Array.from(monthlyMap.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .slice(-6)
      .map(([month, count]) => ({ month: month.slice(5), count }));

    setDashboardData(prev => ({
      ...prev,
      contacts: {
        ...prev.contacts,
        total: snapshot.size,
        unread,
        read,
        spam,
        archived,
        responseRate:
          snapshot.size > 0 ? ((read / snapshot.size) * 100).toFixed(1) : 0,
        monthlyData,
        recentMessages: recentMessages.slice(0, 5),
      },
    }));
  }, []);

  const processHireData = useCallback(snapshot => {
    const monthlyMap = new Map();
    const budgetDist = { '1k-3k': 0, '3k-5k': 0, '5k-10k': 0, '10k+': 0 };
    const recentInquiries = [];
    let pending = 0,
      inReview = 0,
      responded = 0,
      approved = 0,
      declined = 0,
      completed = 0,
      archived = 0;

    snapshot.forEach(doc => {
      const data = doc.data();
      const createdAt = data.createdAt?.toDate?.() || new Date(data.createdAt);
      const monthKey = `${createdAt.getFullYear()}-${String(createdAt.getMonth() + 1).padStart(2, '0')}`;
      monthlyMap.set(monthKey, (monthlyMap.get(monthKey) || 0) + 1);

      if (data.isArchived) archived++;
      else if (data.status === 'pending') pending++;
      else if (data.status === 'in-review') inReview++;
      else if (data.status === 'responded') responded++;
      else if (data.status === 'approved') approved++;
      else if (data.status === 'declined') declined++;
      else if (data.status === 'completed') completed++;

      if (data.budget && budgetDist[data.budget] !== undefined)
        budgetDist[data.budget]++;

      recentInquiries.push({
        id: doc.id,
        name: data.name || 'Anonymous',
        projectType: data.projectType || 'Not specified',
        status: data.status || 'pending',
        createdAt,
      });
    });

    const monthlyData = Array.from(monthlyMap.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .slice(-6)
      .map(([month, count]) => ({ month: month.slice(5), count }));

    const conversionRate =
      snapshot.size > 0 ? ((approved / snapshot.size) * 100).toFixed(1) : 0;

    setDashboardData(prev => ({
      ...prev,
      hires: {
        total: snapshot.size,
        pending,
        inReview,
        responded,
        approved,
        declined,
        completed,
        archived,
        conversionRate,
        budgetDistribution: budgetDist,
        monthlyData,
        recentInquiries: recentInquiries.slice(0, 5),
      },
    }));
  }, []);

  const processReadyMadeInquiries = useCallback(snapshot => {
    const monthlyMap = new Map();
    let pending = 0,
      responded = 0,
      approved = 0,
      declined = 0;

    snapshot.forEach(doc => {
      const data = doc.data();
      const createdAt = data.createdAt?.toDate?.() || new Date(data.createdAt);
      const monthKey = `${createdAt.getFullYear()}-${String(createdAt.getMonth() + 1).padStart(2, '0')}`;
      monthlyMap.set(monthKey, (monthlyMap.get(monthKey) || 0) + 1);

      if (data.status === 'pending') pending++;
      else if (data.status === 'responded') responded++;
      else if (data.status === 'approved') approved++;
      else if (data.status === 'declined') declined++;
    });

    const monthlyData = Array.from(monthlyMap.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .slice(-6)
      .map(([month, count]) => ({ month: month.slice(5), count }));

    setDashboardData(prev => ({
      ...prev,
      readyMadeInquiries: {
        total: snapshot.size,
        pending,
        responded,
        approved,
        declined,
        monthlyData,
      },
    }));
  }, []);

  const processSubscriptionInquiries = useCallback(snapshot => {
    const monthlyMap = new Map();
    let pending = 0,
      responded = 0,
      approved = 0,
      declined = 0;
    let monthly = 0,
      yearly = 0;

    snapshot.forEach(doc => {
      const data = doc.data();
      const createdAt = data.createdAt?.toDate?.() || new Date(data.createdAt);
      const monthKey = `${createdAt.getFullYear()}-${String(createdAt.getMonth() + 1).padStart(2, '0')}`;
      monthlyMap.set(monthKey, (monthlyMap.get(monthKey) || 0) + 1);

      if (data.status === 'pending') pending++;
      else if (data.status === 'responded') responded++;
      else if (data.status === 'approved') approved++;
      else if (data.status === 'declined') declined++;

      if (data.planData?.billingCycle === 'monthly') monthly++;
      if (data.planData?.billingCycle === 'yearly') yearly++;
    });

    const monthlyData = Array.from(monthlyMap.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .slice(-6)
      .map(([month, count]) => ({ month: month.slice(5), count }));

    setDashboardData(prev => ({
      ...prev,
      subscriptionInquiries: {
        total: snapshot.size,
        pending,
        responded,
        approved,
        declined,
        monthly,
        yearly,
        monthlyData,
      },
    }));
  }, []);

  // Process portfolio data from usePortfolio hook - Memoized to prevent re-renders
  const processPortfolioData = useCallback(() => {
    const portfolioProjects = projects || [];

    if (portfolioProjects.length === 0) {
      setDashboardData(prev => ({
        ...prev,
        portfolio: {
          total: 0,
          byCategory: { branding: 0, product: 0, others: 0 },
          featured: 0,
          recentProjects: [],
        },
      }));
      return;
    }

    const byCategory = { branding: 0, product: 0, others: 0 };
    let featured = 0;

    portfolioProjects.forEach(project => {
      if (project.category === 'branding') byCategory.branding++;
      else if (project.category === 'product') byCategory.product++;
      else byCategory.others++;
      if (project.featured) featured++;
    });

    const recentProjects = [...portfolioProjects]
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      .slice(0, 5)
      .map(p => ({
        id: p.id,
        title: p.title,
        category: p.category,
        imageUrl: p.imageUrl || p.thumbnailUrl || p.images?.[0]?.url || '',
      }));

    setDashboardData(prev => ({
      ...prev,
      portfolio: {
        total: portfolioProjects.length,
        byCategory,
        featured,
        recentProjects,
      },
    }));
  }, [projects]);

  // Run portfolio processing when projects change
  useEffect(() => {
    processPortfolioData();
  }, [processPortfolioData]);

  // Setup real-time listeners - Only once
  const setupRealtimeSubscriptions = useCallback(() => {
    // Clean up existing subscriptions
    subscriptionsRef.current.forEach(unsubscribe => {
      if (typeof unsubscribe === 'function') unsubscribe();
    });
    subscriptionsRef.current = [];

    const newSubscriptions = [];

    // Contact messages
    const contactsQuery = query(
      collection(db, 'contactMessages'),
      orderBy('createdAt', 'desc'),
    );
    newSubscriptions.push(onSnapshot(contactsQuery, processContactData));

    // Hire inquiries
    const hiresQuery = query(
      collection(db, 'hireInquiries'),
      orderBy('createdAt', 'desc'),
    );
    newSubscriptions.push(onSnapshot(hiresQuery, processHireData));

    // Ready-made inquiries
    const readyMadeQuery = query(
      collection(db, 'readyMadeInquiries'),
      orderBy('createdAt', 'desc'),
    );
    newSubscriptions.push(
      onSnapshot(readyMadeQuery, processReadyMadeInquiries),
    );

    // Subscription inquiries
    const subscriptionQuery = query(
      collection(db, 'subscriptionInquiries'),
      orderBy('createdAt', 'desc'),
    );
    newSubscriptions.push(
      onSnapshot(subscriptionQuery, processSubscriptionInquiries),
    );

    subscriptionsRef.current = newSubscriptions;
  }, [
    processContactData,
    processHireData,
    processReadyMadeInquiries,
    processSubscriptionInquiries,
  ]);

  // Initial load - Only once
  useEffect(() => {
    if (isInitializedRef.current) return;
    isInitializedRef.current = true;

    const initialize = async () => {
      setDashboardData(prev => ({ ...prev, loading: true }));
      try {
        setupRealtimeSubscriptions();
        await Promise.all([
          fetchReadyMadeServicesStats(),
          fetchSubscriptionPlansStats(),
          fetchReviewsData(),
          fetchBlogAnalytics(),
        ]);
        setDashboardData(prev => ({
          ...prev,
          lastUpdated: new Date(),
          loading: false,
        }));
      } catch (error) {
        console.error('Error initializing dashboard:', error);
        setDashboardData(prev => ({ ...prev, loading: false }));
      }
    };
    initialize();

    return () => {
      subscriptionsRef.current.forEach(unsubscribe => {
        if (typeof unsubscribe === 'function') unsubscribe();
      });
    };
  }, [
    setupRealtimeSubscriptions,
    fetchReviewsData,
    fetchBlogAnalytics,
    fetchReadyMadeServicesStats,
    fetchSubscriptionPlansStats,
  ]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await Promise.all([
        fetchReadyMadeServicesStats(),
        fetchSubscriptionPlansStats(),
        fetchReviewsData(),
        fetchBlogAnalytics(),
        refreshPortfolio(),
      ]);
      setDashboardData(prev => ({ ...prev, lastUpdated: new Date() }));
    } catch (error) {
      console.error('Error refreshing dashboard:', error);
    } finally {
      setTimeout(() => setRefreshing(false), 500);
    }
  };

  // ==================== MEMOIZED CHART DATA ====================
  // This prevents unnecessary re-renders of charts

  const getLineOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
          labels: { color: isDark ? '#f1f5f9' : '#1e293b' },
        },
        tooltip: {
          backgroundColor: isDark ? '#1e293b' : '#ffffff',
          titleColor: isDark ? '#f1f5f9' : '#1e293b',
          bodyColor: isDark ? '#94a3b8' : '#64748b',
          borderColor: isDark ? '#334155' : '#e2e8f0',
          borderWidth: 1,
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          grid: { color: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' },
          ticks: { color: isDark ? '#f1f5f9' : '#1e293b' },
        },
        x: {
          grid: { display: false },
          ticks: { color: isDark ? '#f1f5f9' : '#1e293b' },
        },
      },
    }),
    [isDark],
  );

  const getPieOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            color: isDark ? '#f1f5f9' : '#1e293b',
            font: { size: 10 },
          },
        },
        tooltip: {
          backgroundColor: isDark ? '#1e293b' : '#ffffff',
          titleColor: isDark ? '#f1f5f9' : '#1e293b',
          bodyColor: isDark ? '#94a3b8' : '#64748b',
        },
      },
    }),
    [isDark],
  );

  // Memoized chart data
  const categoryChartData = useMemo(
    () => ({
      labels: ['Brand Design', 'Product Design', 'Other Works'],
      datasets: [
        {
          data: [
            dashboardData.portfolio.byCategory.branding || 0,
            dashboardData.portfolio.byCategory.product || 0,
            dashboardData.portfolio.byCategory.others || 0,
          ],
          backgroundColor: [
            chartColors.purple,
            chartColors.secondary,
            chartColors.cyan,
          ],
          borderWidth: 0,
        },
      ],
    }),
    [dashboardData.portfolio.byCategory],
  );

  const contactTrendData = useMemo(
    () => ({
      labels:
        dashboardData.contacts.monthlyData?.length > 0
          ? dashboardData.contacts.monthlyData.map(d => d.month)
          : ['No Data'],
      datasets: [
        {
          label: 'Messages',
          data:
            dashboardData.contacts.monthlyData?.length > 0
              ? dashboardData.contacts.monthlyData.map(d => d.count)
              : [0],
          borderColor: chartColors.info,
          backgroundColor: `${chartColors.info}20`,
          fill: true,
          tension: 0.4,
          pointRadius: 3,
          pointBackgroundColor: chartColors.info,
        },
      ],
    }),
    [dashboardData.contacts.monthlyData],
  );

  const hireTrendData = useMemo(
    () => ({
      labels:
        dashboardData.hires.monthlyData?.length > 0
          ? dashboardData.hires.monthlyData.map(d => d.month)
          : ['No Data'],
      datasets: [
        {
          label: 'Inquiries',
          data:
            dashboardData.hires.monthlyData?.length > 0
              ? dashboardData.hires.monthlyData.map(d => d.count)
              : [0],
          borderColor: chartColors.success,
          backgroundColor: `${chartColors.success}20`,
          fill: true,
          tension: 0.4,
          pointRadius: 3,
          pointBackgroundColor: chartColors.success,
        },
      ],
    }),
    [dashboardData.hires.monthlyData],
  );

  const readyMadeTrendData = useMemo(
    () => ({
      labels:
        dashboardData.readyMadeInquiries.monthlyData?.length > 0
          ? dashboardData.readyMadeInquiries.monthlyData.map(d => d.month)
          : ['No Data'],
      datasets: [
        {
          label: 'Inquiries',
          data:
            dashboardData.readyMadeInquiries.monthlyData?.length > 0
              ? dashboardData.readyMadeInquiries.monthlyData.map(d => d.count)
              : [0],
          borderColor: chartColors.warning,
          backgroundColor: `${chartColors.warning}20`,
          fill: true,
          tension: 0.4,
          pointRadius: 3,
          pointBackgroundColor: chartColors.warning,
        },
      ],
    }),
    [dashboardData.readyMadeInquiries.monthlyData],
  );

  const subscriptionTrendData = useMemo(
    () => ({
      labels:
        dashboardData.subscriptionInquiries.monthlyData?.length > 0
          ? dashboardData.subscriptionInquiries.monthlyData.map(d => d.month)
          : ['No Data'],
      datasets: [
        {
          label: 'Inquiries',
          data:
            dashboardData.subscriptionInquiries.monthlyData?.length > 0
              ? dashboardData.subscriptionInquiries.monthlyData.map(
                  d => d.count,
                )
              : [0],
          borderColor: chartColors.primary,
          backgroundColor: `${chartColors.primary}20`,
          fill: true,
          tension: 0.4,
          pointRadius: 3,
          pointBackgroundColor: chartColors.primary,
        },
      ],
    }),
    [dashboardData.subscriptionInquiries.monthlyData],
  );

  const reviewsTrendData = useMemo(
    () => ({
      labels:
        dashboardData.reviews.monthlyData?.length > 0
          ? dashboardData.reviews.monthlyData.map(d => d.month)
          : ['No Data'],
      datasets: [
        {
          label: 'Reviews',
          data:
            dashboardData.reviews.monthlyData?.length > 0
              ? dashboardData.reviews.monthlyData.map(d => d.count)
              : [0],
          borderColor: chartColors.secondary,
          backgroundColor: `${chartColors.secondary}20`,
          fill: true,
          tension: 0.4,
          pointRadius: 3,
          pointBackgroundColor: chartColors.secondary,
        },
      ],
    }),
    [dashboardData.reviews.monthlyData],
  );

  const blogTrendData = useMemo(
    () => ({
      labels:
        dashboardData.blog.monthlyData?.length > 0
          ? dashboardData.blog.monthlyData.map(d => d.month)
          : ['No Data'],
      datasets: [
        {
          label: 'Posts',
          data:
            dashboardData.blog.monthlyData?.length > 0
              ? dashboardData.blog.monthlyData.map(d => d.count)
              : [0],
          borderColor: chartColors.primary,
          backgroundColor: `${chartColors.primary}20`,
          fill: true,
          tension: 0.4,
          pointRadius: 3,
          pointBackgroundColor: chartColors.primary,
        },
      ],
    }),
    [dashboardData.blog.monthlyData],
  );

  const blogCommentTrendData = useMemo(
    () => ({
      labels:
        dashboardData.blogComments.monthlyData?.length > 0
          ? dashboardData.blogComments.monthlyData.map(d => d.month)
          : ['No Data'],
      datasets: [
        {
          label: 'Comments',
          data:
            dashboardData.blogComments.monthlyData?.length > 0
              ? dashboardData.blogComments.monthlyData.map(d => d.count)
              : [0],
          borderColor: chartColors.info,
          backgroundColor: `${chartColors.info}20`,
          fill: true,
          tension: 0.4,
          pointRadius: 3,
          pointBackgroundColor: chartColors.info,
        },
      ],
    }),
    [dashboardData.blogComments.monthlyData],
  );

  const budgetChartData = useMemo(
    () => ({
      labels: ['$1K-3K', '$3K-5K', '$5K-10K', '$10K+'],
      datasets: [
        {
          data: [
            dashboardData.hires.budgetDistribution['1k-3k'] || 0,
            dashboardData.hires.budgetDistribution['3k-5k'] || 0,
            dashboardData.hires.budgetDistribution['5k-10k'] || 0,
            dashboardData.hires.budgetDistribution['10k+'] || 0,
          ],
          backgroundColor: [
            chartColors.warning,
            chartColors.info,
            chartColors.success,
            chartColors.purple,
          ],
          borderWidth: 0,
        },
      ],
    }),
    [dashboardData.hires.budgetDistribution],
  );

  const readyMadeStatusData = useMemo(
    () => ({
      labels: ['Pending', 'Responded', 'Approved', 'Declined'],
      datasets: [
        {
          data: [
            dashboardData.readyMadeInquiries.pending || 0,
            dashboardData.readyMadeInquiries.responded || 0,
            dashboardData.readyMadeInquiries.approved || 0,
            dashboardData.readyMadeInquiries.declined || 0,
          ],
          backgroundColor: [
            chartColors.warning,
            chartColors.info,
            chartColors.success,
            chartColors.error,
          ],
          borderWidth: 0,
        },
      ],
    }),
    [dashboardData.readyMadeInquiries],
  );

  const subscriptionStatusData = useMemo(
    () => ({
      labels: ['Pending', 'Responded', 'Approved', 'Declined'],
      datasets: [
        {
          data: [
            dashboardData.subscriptionInquiries.pending || 0,
            dashboardData.subscriptionInquiries.responded || 0,
            dashboardData.subscriptionInquiries.approved || 0,
            dashboardData.subscriptionInquiries.declined || 0,
          ],
          backgroundColor: [
            chartColors.warning,
            chartColors.info,
            chartColors.success,
            chartColors.error,
          ],
          borderWidth: 0,
        },
      ],
    }),
    [dashboardData.subscriptionInquiries],
  );

  const reviewsRatingDistributionData = useMemo(
    () => ({
      labels: ['5 Star', '4 Star', '3 Star', '2 Star', '1 Star'],
      datasets: [
        {
          data: [
            dashboardData.reviews.ratingDistribution[5] || 0,
            dashboardData.reviews.ratingDistribution[4] || 0,
            dashboardData.reviews.ratingDistribution[3] || 0,
            dashboardData.reviews.ratingDistribution[2] || 0,
            dashboardData.reviews.ratingDistribution[1] || 0,
          ],
          backgroundColor: [
            chartColors.success,
            chartColors.info,
            chartColors.warning,
            chartColors.orange,
            chartColors.error,
          ],
          borderWidth: 0,
        },
      ],
    }),
    [dashboardData.reviews.ratingDistribution],
  );

  // Pie colors for blog categories
  const PIE_COLORS = [
    chartColors.primary,
    chartColors.secondary,
    chartColors.success,
    chartColors.warning,
    chartColors.info,
    chartColors.cyan,
    chartColors.purple,
    chartColors.orange,
  ];

  // Blog Category Distribution - Memoized
  const blogCategoryLabels = useMemo(
    () => Object.keys(dashboardData.blog.categoryDistribution || {}),
    [dashboardData.blog.categoryDistribution],
  );

  const blogCategoryValues = useMemo(
    () => Object.values(dashboardData.blog.categoryDistribution || {}),
    [dashboardData.blog.categoryDistribution],
  );

  const blogCategoryData = useMemo(
    () => ({
      labels: blogCategoryLabels.length > 0 ? blogCategoryLabels : ['No Data'],
      datasets: [
        {
          data: blogCategoryValues.length > 0 ? blogCategoryValues : [1],
          backgroundColor:
            blogCategoryValues.length > 0
              ? blogCategoryLabels.map(
                  (_, i) => PIE_COLORS[i % PIE_COLORS.length],
                )
              : [chartColors.primary],
          borderWidth: 0,
        },
      ],
    }),
    [blogCategoryLabels, blogCategoryValues],
  );

  // Blog Comment Reaction Distribution - Memoized
  const blogReactionData = useMemo(
    () => ({
      labels: ['Like', 'Love', 'Haha', 'Sad', 'Angry'],
      datasets: [
        {
          data: [
            dashboardData.blogComments.reactionDistribution?.like || 0,
            dashboardData.blogComments.reactionDistribution?.love || 0,
            dashboardData.blogComments.reactionDistribution?.haha || 0,
            dashboardData.blogComments.reactionDistribution?.sad || 0,
            dashboardData.blogComments.reactionDistribution?.angry || 0,
          ],
          backgroundColor: [
            chartColors.info,
            chartColors.secondary,
            chartColors.warning,
            chartColors.purple,
            chartColors.error,
          ],
          borderWidth: 0,
        },
      ],
    }),
    [dashboardData.blogComments.reactionDistribution],
  );

  // Stats cards data - Memoized
  const statsCards = useMemo(
    () => [
      {
        title: 'PROJECTS',
        value: dashboardData.portfolio.total,
        icon: FiGrid,
        onClick: () => navigate('/admin/portfolio'),
        subtitle: `${dashboardData.portfolio.featured} featured`,
      },
      {
        title: 'BLOG POSTS',
        value: dashboardData.blog.total,
        icon: FiBookOpen,
        onClick: () => navigate('/admin/blog'),
        subtitle: `${dashboardData.blog.published} published`,
      },
      {
        title: 'READY-MADE',
        value: dashboardData.readyMadeServices.total,
        icon: FiPackage,
        onClick: () => navigate('/admin/ready-made-services'),
        subtitle: `${dashboardData.readyMadeServices.active} active`,
      },
      {
        title: 'SUBSCRIPTION PLANS',
        value: dashboardData.subscriptionPlans.total,
        icon: FiCalendar,
        onClick: () => navigate('/admin/subscription-plans'),
        subtitle: `${dashboardData.subscriptionPlans.active} active`,
      },
      {
        title: 'CUSTOM HIRE',
        value: dashboardData.hires.total,
        icon: FiBriefcase,
        onClick: () => navigate('/admin/inquiries/hire'),
        subtitle: `${dashboardData.hires.conversionRate}% conversion`,
      },
      {
        title: 'PACKAGE INQ',
        value: dashboardData.readyMadeInquiries.total,
        icon: FiShoppingBag,
        onClick: () => navigate('/admin/inquiries/ready-made'),
        subtitle: `${dashboardData.readyMadeInquiries.pending} pending`,
      },
      {
        title: 'SUB INQ',
        value: dashboardData.subscriptionInquiries.total,
        icon: FiUsers,
        onClick: () => navigate('/admin/inquiries/subscription'),
        subtitle: `${dashboardData.subscriptionInquiries.pending} pending`,
      },
      {
        title: 'CONTACT MSG',
        value: dashboardData.contacts.total,
        icon: FiMail,
        onClick: () => navigate('/admin/inquiries/contact'),
        subtitle: `${dashboardData.contacts.unread} unread`,
      },
      {
        title: 'BLOG COMMENTS',
        value: dashboardData.blogComments.total,
        icon: FiMessageSquare,
        onClick: () => navigate('/admin/blog'),
        subtitle: `${dashboardData.blogComments.totalReactions} reactions`,
      },
      {
        title: 'RESPONSE RATE',
        value: parseFloat(dashboardData.contacts.responseRate),
        icon: FiActivity,
        onClick: () => navigate('/admin/inquiries/contact'),
        suffix: '%',
        subtitle: `${dashboardData.contacts.read} responded`,
      },
      {
        title: 'TOTAL REVIEWS',
        value: dashboardData.reviews.total,
        icon: FiStar,
        onClick: () => navigate('/admin/reviews'),
        subtitle: `${dashboardData.reviews.averageRating} ⭐ avg`,
      },
    ],
    [dashboardData, navigate],
  );

  // Total interactions - Memoized
  const totalInteractions = useMemo(
    () =>
      dashboardData.contacts.total +
      dashboardData.hires.total +
      dashboardData.readyMadeInquiries.total +
      dashboardData.subscriptionInquiries.total,
    [dashboardData],
  );

  const activeInquiries = useMemo(
    () =>
      dashboardData.hires.pending +
      dashboardData.hires.inReview +
      dashboardData.readyMadeInquiries.pending +
      dashboardData.subscriptionInquiries.pending,
    [dashboardData],
  );

  if (dashboardData.loading || portfolioLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <div
          className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin"
          style={{ borderColor: 'var(--text)' }}
        />
        <p className="text-sm" style={{ color: 'var(--text)' }}>
          Loading dashboard data...
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-5 pb-8">
      {/* Welcome Section */}
      <div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1
              className="text-xl md:text-2xl font-bold"
              style={{ color: 'var(--text)' }}
            >
              Welcome back, {currentUser?.displayName?.split(' ')[0] || 'Admin'}
              !
            </h1>
            <p className="text-xs mt-0.5" style={{ color: 'var(--text)' }}>
              Here's what's happening with your business today.
            </p>
            <div className="flex items-center gap-2 mt-1">
              <div
                className="w-1.5 h-1.5 rounded-full animate-pulse"
                style={{ backgroundColor: '#10b981' }}
              />
              <p className="text-[10px]" style={{ color: 'var(--text)' }}>
                Live data • Last updated:{' '}
                {dashboardData.lastUpdated.toLocaleTimeString()}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-all hover:opacity-80 text-xs"
              style={{
                border: '1px solid var(--border)',
                backgroundColor: 'transparent',
                color: 'var(--text)',
              }}
            >
              <FiRefreshCw
                className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`}
              />
              <span>Refresh</span>
            </button>
            <NavLink
              to="/admin/settings"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-all hover:opacity-80 text-xs"
              style={{
                border: '1px solid var(--border)',
                backgroundColor: 'transparent',
                color: 'var(--text)',
              }}
            >
              <FiSettings className="w-3.5 h-3.5" />
              <span>Settings</span>
            </NavLink>
          </div>
        </div>

        {/* Key Metrics Summary */}
        <div
          className="grid grid-cols-5 gap-2 mt-3 p-2.5 rounded-lg"
          style={{ backgroundColor: 'var(--border)' }}
        >
          <div className="text-center">
            <div
              className="text-lg font-bold"
              style={{ color: chartColors.purple }}
            >
              {totalInteractions}
            </div>
            <div className="text-[10px]" style={{ color: 'var(--text)' }}>
              Total Interactions
            </div>
          </div>
          <div
            className="text-center border-l border-r"
            style={{ borderColor: 'var(--border-light)' }}
          >
            <div
              className="text-lg font-bold"
              style={{ color: chartColors.warning }}
            >
              {activeInquiries}
            </div>
            <div className="text-[10px]" style={{ color: 'var(--text)' }}>
              Active Inquiries
            </div>
          </div>
          <div className="text-center">
            <div
              className="text-lg font-bold"
              style={{ color: chartColors.info }}
            >
              {dashboardData.contacts.unread}
            </div>
            <div className="text-[10px]" style={{ color: 'var(--text)' }}>
              Unread Messages
            </div>
          </div>
          <div className="text-center">
            <div
              className="text-lg font-bold"
              style={{ color: chartColors.secondary }}
            >
              {dashboardData.reviews.pending}
            </div>
            <div className="text-[10px]" style={{ color: 'var(--text)' }}>
              Pending Reviews
            </div>
          </div>
          <div className="text-center">
            <div
              className="text-lg font-bold"
              style={{ color: chartColors.primary }}
            >
              {dashboardData.blog.published}
            </div>
            <div className="text-[10px]" style={{ color: 'var(--text)' }}>
              Published Blogs
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 2xl:grid-cols-11 gap-2">
        {statsCards.map((card, index) => (
          <StatsCard
            key={index}
            title={card.title}
            value={card.value}
            icon={card.icon}
            onClick={card.onClick}
            subtitle={card.subtitle}
          />
        ))}
      </div>

      {/* Portfolio Analytics Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Category Distribution */}
        <div
          className="rounded-xl p-4 border"
          style={{ backgroundColor: 'var(--bg)', borderColor: 'var(--border)' }}
        >
          <div className="flex items-center justify-between mb-3">
            <h3
              className="text-sm font-semibold flex items-center gap-1.5"
              style={{ color: 'var(--text)' }}
            >
              <FiPieChartIcon
                className="w-4 h-4"
                style={{ color: chartColors.primary }}
              />{' '}
              Portfolio by Category
            </h3>
            <NavLink
              to="/admin/portfolio"
              className="text-[10px] flex items-center gap-1 transition-colors hover:opacity-70"
              style={{ color: 'var(--text)' }}
            >
              Manage <FiArrowRight className="w-2.5 h-2.5" />
            </NavLink>
          </div>
          <div style={{ height: '180px', width: '100%', position: 'relative' }}>
            <Doughnut data={categoryChartData} options={getPieOptions} />
          </div>
          <div
            className="grid grid-cols-3 gap-2 mt-3 pt-2 border-t"
            style={{ borderColor: 'var(--border)' }}
          >
            <div className="text-center">
              <p className="text-[10px]" style={{ color: 'var(--text)' }}>
                Brand Design
              </p>
              <p
                className="text-base font-bold"
                style={{ color: chartColors.purple }}
              >
                {dashboardData.portfolio.byCategory.branding}
              </p>
            </div>
            <div className="text-center">
              <p className="text-[10px]" style={{ color: 'var(--text)' }}>
                Product Design
              </p>
              <p
                className="text-base font-bold"
                style={{ color: chartColors.secondary }}
              >
                {dashboardData.portfolio.byCategory.product}
              </p>
            </div>
            <div className="text-center">
              <p className="text-[10px]" style={{ color: 'var(--text)' }}>
                Other Works
              </p>
              <p
                className="text-base font-bold"
                style={{ color: chartColors.cyan }}
              >
                {dashboardData.portfolio.byCategory.others}
              </p>
            </div>
          </div>
        </div>

        {/* Recent Projects */}
        <div
          className="rounded-xl p-4 border"
          style={{ backgroundColor: 'var(--bg)', borderColor: 'var(--border)' }}
        >
          <h3
            className="text-sm font-semibold mb-3 flex items-center gap-1.5"
            style={{ color: 'var(--text)' }}
          >
            <FiEye className="w-4 h-4" style={{ color: chartColors.cyan }} />{' '}
            Recent Projects
          </h3>
          <div className="space-y-2">
            {dashboardData.portfolio.recentProjects.length === 0 ? (
              <div className="text-center py-6">
                <p className="text-xs" style={{ color: 'var(--text)' }}>
                  No projects yet
                </p>
              </div>
            ) : (
              dashboardData.portfolio.recentProjects.map(project => (
                <div
                  key={project.id}
                  className="flex items-center gap-2 p-2 rounded-lg transition-all hover:translate-x-0.5 group"
                  style={{ backgroundColor: 'var(--border)' }}
                >
                  <img
                    src={project.imageUrl}
                    alt={project.title}
                    className="w-8 h-8 rounded-lg object-cover"
                  />
                  <div className="flex-1 min-w-0">
                    <p
                      className="text-xs font-medium truncate"
                      style={{ color: 'var(--text)' }}
                    >
                      {project.title}
                    </p>
                    <p
                      className="text-[10px] capitalize"
                      style={{ color: 'var(--text)' }}
                    >
                      {project.category}
                    </p>
                  </div>
                  <NavLink
                    to="/admin/portfolio"
                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <FiArrowRight
                      className="w-3 h-3"
                      style={{ color: 'var(--text)' }}
                    />
                  </NavLink>
                </div>
              ))
            )}
          </div>
          <div
            className="mt-3 pt-2 border-t flex justify-between"
            style={{ borderColor: 'var(--border)' }}
          >
            <span className="text-[10px]" style={{ color: 'var(--text)' }}>
              Total Projects
            </span>
            <span
              className="text-sm font-bold"
              style={{ color: chartColors.primary }}
            >
              {dashboardData.portfolio.total}
            </span>
          </div>
        </div>
      </div>

      {/* Blog Analytics Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Blog Overview */}
        <div
          className="rounded-xl p-4 border"
          style={{ backgroundColor: 'var(--bg)', borderColor: 'var(--border)' }}
        >
          <div className="flex items-center justify-between mb-3">
            <h3
              className="text-sm font-semibold flex items-center gap-1.5"
              style={{ color: 'var(--text)' }}
            >
              <FiBookOpen
                className="w-4 h-4"
                style={{ color: chartColors.primary }}
              />{' '}
              Blog Overview
            </h3>
            <NavLink
              to="/admin/blog"
              className="text-[10px] flex items-center gap-1 transition-colors hover:opacity-70"
              style={{ color: 'var(--text)' }}
            >
              Manage <FiArrowRight className="w-2.5 h-2.5" />
            </NavLink>
          </div>
          <div className="grid grid-cols-3 gap-2 mb-3">
            <div
              className="text-center p-2 rounded-lg"
              style={{ backgroundColor: 'var(--border)' }}
            >
              <p className="text-[10px]" style={{ color: 'var(--text)' }}>
                Published
              </p>
              <p
                className="text-lg font-bold"
                style={{ color: chartColors.success }}
              >
                {dashboardData.blog.published}
              </p>
            </div>
            <div
              className="text-center p-2 rounded-lg"
              style={{ backgroundColor: 'var(--border)' }}
            >
              <p className="text-[10px]" style={{ color: 'var(--text)' }}>
                Drafts
              </p>
              <p
                className="text-lg font-bold"
                style={{ color: chartColors.warning }}
              >
                {dashboardData.blog.draft}
              </p>
            </div>
            <div
              className="text-center p-2 rounded-lg"
              style={{ backgroundColor: 'var(--border)' }}
            >
              <p className="text-[10px]" style={{ color: 'var(--text)' }}>
                Scheduled
              </p>
              <p
                className="text-lg font-bold"
                style={{ color: chartColors.cyan }}
              >
                {dashboardData.blog.scheduled}
              </p>
            </div>
          </div>
          <div
            className="grid grid-cols-3 gap-2"
            style={{ borderTop: `1px solid var(--border)`, paddingTop: '12px' }}
          >
            <div className="text-center">
              <p className="text-[10px]" style={{ color: 'var(--text)' }}>
                Total Views
              </p>
              <p
                className="text-base font-bold"
                style={{ color: chartColors.info }}
              >
                {dashboardData.blog.totalViews.toLocaleString()}
              </p>
            </div>
            <div className="text-center">
              <p className="text-[10px]" style={{ color: 'var(--text)' }}>
                Comments
              </p>
              <p
                className="text-base font-bold"
                style={{ color: chartColors.warning }}
              >
                {dashboardData.blog.totalComments}
              </p>
            </div>
            <div className="text-center">
              <p className="text-[10px]" style={{ color: 'var(--text)' }}>
                Engagement Rate
              </p>
              <p
                className="text-base font-bold"
                style={{ color: chartColors.secondary }}
              >
                {dashboardData.blog.engagementRate}%
              </p>
            </div>
          </div>
        </div>

        {/* Blog Category Distribution */}
        <div
          className="rounded-xl p-4 border"
          style={{ backgroundColor: 'var(--bg)', borderColor: 'var(--border)' }}
        >
          <div className="flex items-center justify-between mb-3">
            <h3
              className="text-sm font-semibold flex items-center gap-1.5"
              style={{ color: 'var(--text)' }}
            >
              <FiFolder
                className="w-4 h-4"
                style={{ color: chartColors.primary }}
              />{' '}
              Blog Category Distribution
            </h3>
            <NavLink
              to="/admin/blog"
              className="text-[10px] flex items-center gap-1 transition-colors hover:opacity-70"
              style={{ color: 'var(--text)' }}
            >
              Manage <FiArrowRight className="w-2.5 h-2.5" />
            </NavLink>
          </div>
          {Object.keys(dashboardData.blog.categoryDistribution || {}).length >
          0 ? (
            <div
              style={{ height: '180px', width: '100%', position: 'relative' }}
            >
              <Doughnut data={blogCategoryData} options={getPieOptions} />
            </div>
          ) : (
            <div className="flex items-center justify-center h-[180px]">
              <p className="text-sm" style={{ color: 'var(--text)' }}>
                No blog category data available
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Blog Comments Analytics Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Blog Comments Overview */}
        <div
          className="rounded-xl p-4 border"
          style={{ backgroundColor: 'var(--bg)', borderColor: 'var(--border)' }}
        >
          <div className="flex items-center justify-between mb-3">
            <h3
              className="text-sm font-semibold flex items-center gap-1.5"
              style={{ color: 'var(--text)' }}
            >
              <FiMessageSquare
                className="w-4 h-4"
                style={{ color: chartColors.info }}
              />{' '}
              Blog Comments Overview
            </h3>
            <NavLink
              to="/admin/blog"
              className="text-[10px] flex items-center gap-1 transition-colors hover:opacity-70"
              style={{ color: 'var(--text)' }}
            >
              Manage <FiArrowRight className="w-2.5 h-2.5" />
            </NavLink>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div
              className="text-center p-2 rounded-lg"
              style={{ backgroundColor: 'var(--border)' }}
            >
              <p className="text-[10px]" style={{ color: 'var(--text)' }}>
                Total Comments
              </p>
              <p
                className="text-lg font-bold"
                style={{ color: chartColors.info }}
              >
                {dashboardData.blogComments.total}
              </p>
            </div>
            <div
              className="text-center p-2 rounded-lg"
              style={{ backgroundColor: 'var(--border)' }}
            >
              <p className="text-[10px]" style={{ color: 'var(--text)' }}>
                Total Reactions
              </p>
              <p
                className="text-lg font-bold"
                style={{ color: chartColors.secondary }}
              >
                {dashboardData.blogComments.totalReactions}
              </p>
            </div>
            <div
              className="text-center p-2 rounded-lg"
              style={{ backgroundColor: 'var(--border)' }}
            >
              <p className="text-[10px]" style={{ color: 'var(--text)' }}>
                Active Commenters
              </p>
              <p
                className="text-lg font-bold"
                style={{ color: chartColors.success }}
              >
                {dashboardData.blogComments.activeCommenters}
              </p>
            </div>
          </div>
          <div
            className="mt-2 pt-2 border-t"
            style={{ borderColor: 'var(--border)' }}
          >
            <p className="text-[10px]" style={{ color: 'var(--text)' }}>
              Avg Reactions per Comment:{' '}
              <span
                className="font-bold"
                style={{ color: chartColors.secondary }}
              >
                {dashboardData.blogComments.avgReactionsPerComment}
              </span>
            </p>
          </div>
        </div>

        {/* Blog Comment Reaction Distribution */}
        <div
          className="rounded-xl p-4 border"
          style={{ backgroundColor: 'var(--bg)', borderColor: 'var(--border)' }}
        >
          <div className="flex items-center justify-between mb-3">
            <h3
              className="text-sm font-semibold flex items-center gap-1.5"
              style={{ color: 'var(--text)' }}
            >
              <FiHeart
                className="w-4 h-4"
                style={{ color: chartColors.secondary }}
              />{' '}
              Comment Reaction Distribution
            </h3>
            <NavLink
              to="/admin/blog"
              className="text-[10px] flex items-center gap-1 transition-colors hover:opacity-70"
              style={{ color: 'var(--text)' }}
            >
              Manage <FiArrowRight className="w-2.5 h-2.5" />
            </NavLink>
          </div>
          {dashboardData.blogComments.total > 0 ? (
            <div
              style={{ height: '180px', width: '100%', position: 'relative' }}
            >
              <Doughnut data={blogReactionData} options={getPieOptions} />
            </div>
          ) : (
            <div className="flex items-center justify-center h-[180px]">
              <p className="text-sm" style={{ color: 'var(--text)' }}>
                No comment reactions yet
              </p>
            </div>
          )}
          <div
            className="grid grid-cols-5 gap-1 mt-2 pt-2 border-t text-center"
            style={{ borderColor: 'var(--border)' }}
          >
            <div>
              <p className="text-[9px]" style={{ color: 'var(--text)' }}>
                👍 Like
              </p>
              <p
                className="text-sm font-bold"
                style={{ color: chartColors.info }}
              >
                {dashboardData.blogComments.reactionDistribution.like || 0}
              </p>
            </div>
            <div>
              <p className="text-[9px]" style={{ color: 'var(--text)' }}>
                ❤️ Love
              </p>
              <p
                className="text-sm font-bold"
                style={{ color: chartColors.secondary }}
              >
                {dashboardData.blogComments.reactionDistribution.love || 0}
              </p>
            </div>
            <div>
              <p className="text-[9px]" style={{ color: 'var(--text)' }}>
                😂 Haha
              </p>
              <p
                className="text-sm font-bold"
                style={{ color: chartColors.warning }}
              >
                {dashboardData.blogComments.reactionDistribution.haha || 0}
              </p>
            </div>
            <div>
              <p className="text-[9px]" style={{ color: 'var(--text)' }}>
                😢 Sad
              </p>
              <p
                className="text-sm font-bold"
                style={{ color: chartColors.purple }}
              >
                {dashboardData.blogComments.reactionDistribution.sad || 0}
              </p>
            </div>
            <div>
              <p className="text-[9px]" style={{ color: 'var(--text)' }}>
                😠 Angry
              </p>
              <p
                className="text-sm font-bold"
                style={{ color: chartColors.error }}
              >
                {dashboardData.blogComments.reactionDistribution.angry || 0}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Top Commenters & Top Commented Blogs */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Top Commenters */}
        <div
          className="rounded-xl p-4 border"
          style={{ backgroundColor: 'var(--bg)', borderColor: 'var(--border)' }}
        >
          <h3
            className="text-sm font-semibold mb-3 flex items-center gap-1.5"
            style={{ color: 'var(--text)' }}
          >
            <FiUsers
              className="w-4 h-4"
              style={{ color: chartColors.success }}
            />{' '}
            Top Blog Commenters
          </h3>
          <div className="space-y-2">
            {dashboardData.blogComments.topCommenters.length === 0 ? (
              <div className="text-center py-6">
                <FiMessageSquare
                  className="w-6 h-6 mx-auto mb-1 opacity-50"
                  style={{ color: 'var(--text)' }}
                />
                <p className="text-xs" style={{ color: 'var(--text)' }}>
                  No commenters yet
                </p>
              </div>
            ) : (
              dashboardData.blogComments.topCommenters.map((commenter, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-2 p-2 rounded-lg transition-all hover:translate-x-0.5"
                  style={{ backgroundColor: 'var(--border)' }}
                >
                  <div
                    className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{
                      backgroundColor: `${chartColors.primary}20`,
                      color: chartColors.primary,
                    }}
                  >
                    {commenter.name?.charAt(0).toUpperCase() || '?'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p
                      className="text-xs font-medium truncate"
                      style={{ color: 'var(--text)' }}
                    >
                      {commenter.name}
                    </p>
                    <p
                      className="text-[10px] truncate"
                      style={{ color: 'var(--text)' }}
                    >
                      {commenter.email}
                    </p>
                  </div>
                  <span
                    className="text-xs font-bold"
                    style={{ color: chartColors.warning }}
                  >
                    {commenter.count}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Top Commented Blogs */}
        <div
          className="rounded-xl p-4 border"
          style={{ backgroundColor: 'var(--bg)', borderColor: 'var(--border)' }}
        >
          <h3
            className="text-sm font-semibold mb-3 flex items-center gap-1.5"
            style={{ color: 'var(--text)' }}
          >
            <FiBookOpen
              className="w-4 h-4"
              style={{ color: chartColors.primary }}
            />{' '}
            Most Commented Blogs
          </h3>
          <div className="space-y-2">
            {dashboardData.blogComments.topCommentedBlogs.length === 0 ? (
              <div className="text-center py-6">
                <FiBookOpen
                  className="w-6 h-6 mx-auto mb-1 opacity-50"
                  style={{ color: 'var(--text)' }}
                />
                <p className="text-xs" style={{ color: 'var(--text)' }}>
                  No blog comments yet
                </p>
              </div>
            ) : (
              dashboardData.blogComments.topCommentedBlogs.map((blog, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between p-2 rounded-lg transition-all hover:translate-x-0.5"
                  style={{ backgroundColor: 'var(--border)' }}
                >
                  <div className="flex-1 min-w-0">
                    <p
                      className="text-xs font-medium truncate"
                      style={{ color: 'var(--text)' }}
                    >
                      {blog.title}
                    </p>
                  </div>
                  <span
                    className="text-xs font-bold"
                    style={{ color: chartColors.info }}
                  >
                    {blog.count} comments
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* All Inquiries Trends - 7 Charts Row */}
      <div className="grid grid-cols-2 lg:grid-cols-7 gap-3">
        {/* Contact Messages Trend */}
        <div
          className="rounded-xl p-3 border"
          style={{ backgroundColor: 'var(--bg)', borderColor: 'var(--border)' }}
        >
          <h3
            className="text-xs font-semibold mb-2 flex items-center gap-1"
            style={{ color: 'var(--text)' }}
          >
            <FiMail className="w-3 h-3" style={{ color: chartColors.info }} />{' '}
            Contact
          </h3>
          <div style={{ height: '80px' }}>
            <Line data={contactTrendData} options={getLineOptions} />
          </div>
          <div
            className="flex justify-between mt-1 pt-1 border-t"
            style={{ borderColor: 'var(--border)' }}
          >
            <span className="text-[9px]" style={{ color: 'var(--text)' }}>
              {dashboardData.contacts.total}
            </span>
            <span className="text-[9px]" style={{ color: chartColors.warning }}>
              {dashboardData.contacts.unread} unread
            </span>
          </div>
        </div>

        {/* Custom Hire Trend */}
        <div
          className="rounded-xl p-3 border"
          style={{ backgroundColor: 'var(--bg)', borderColor: 'var(--border)' }}
        >
          <h3
            className="text-xs font-semibold mb-2 flex items-center gap-1"
            style={{ color: 'var(--text)' }}
          >
            <FiBriefcase
              className="w-3 h-3"
              style={{ color: chartColors.success }}
            />{' '}
            Hire
          </h3>
          <div style={{ height: '80px' }}>
            <Line data={hireTrendData} options={getLineOptions} />
          </div>
          <div
            className="flex justify-between mt-1 pt-1 border-t"
            style={{ borderColor: 'var(--border)' }}
          >
            <span className="text-[9px]" style={{ color: 'var(--text)' }}>
              {dashboardData.hires.total}
            </span>
            <span className="text-[9px]" style={{ color: chartColors.warning }}>
              {dashboardData.hires.pending} pend
            </span>
          </div>
        </div>

        {/* Package Inquiries Trend */}
        <div
          className="rounded-xl p-3 border"
          style={{ backgroundColor: 'var(--bg)', borderColor: 'var(--border)' }}
        >
          <h3
            className="text-xs font-semibold mb-2 flex items-center gap-1"
            style={{ color: 'var(--text)' }}
          >
            <FiShoppingBag
              className="w-3 h-3"
              style={{ color: chartColors.warning }}
            />{' '}
            Package
          </h3>
          <div style={{ height: '80px' }}>
            <Line data={readyMadeTrendData} options={getLineOptions} />
          </div>
          <div
            className="flex justify-between mt-1 pt-1 border-t"
            style={{ borderColor: 'var(--border)' }}
          >
            <span className="text-[9px]" style={{ color: 'var(--text)' }}>
              {dashboardData.readyMadeInquiries.total}
            </span>
            <span className="text-[9px]" style={{ color: chartColors.warning }}>
              {dashboardData.readyMadeInquiries.pending} pend
            </span>
          </div>
        </div>

        {/* Subscription Inquiries Trend */}
        <div
          className="rounded-xl p-3 border"
          style={{ backgroundColor: 'var(--bg)', borderColor: 'var(--border)' }}
        >
          <h3
            className="text-xs font-semibold mb-2 flex items-center gap-1"
            style={{ color: 'var(--text)' }}
          >
            <FiCalendar
              className="w-3 h-3"
              style={{ color: chartColors.purple }}
            />{' '}
            Subscriptions
          </h3>
          <div style={{ height: '80px' }}>
            <Line data={subscriptionTrendData} options={getLineOptions} />
          </div>
          <div
            className="flex justify-between mt-1 pt-1 border-t"
            style={{ borderColor: 'var(--border)' }}
          >
            <span className="text-[9px]" style={{ color: 'var(--text)' }}>
              {dashboardData.subscriptionInquiries.total}
            </span>
            <span className="text-[9px]" style={{ color: chartColors.warning }}>
              {dashboardData.subscriptionInquiries.pending} pend
            </span>
          </div>
        </div>

        {/* Reviews Trend */}
        <div
          className="rounded-xl p-3 border"
          style={{ backgroundColor: 'var(--bg)', borderColor: 'var(--border)' }}
        >
          <h3
            className="text-xs font-semibold mb-2 flex items-center gap-1"
            style={{ color: 'var(--text)' }}
          >
            <FiStar
              className="w-3 h-3"
              style={{ color: chartColors.secondary }}
            />{' '}
            Reviews
          </h3>
          <div style={{ height: '80px' }}>
            <Line data={reviewsTrendData} options={getLineOptions} />
          </div>
          <div
            className="flex justify-between mt-1 pt-1 border-t"
            style={{ borderColor: 'var(--border)' }}
          >
            <span className="text-[9px]" style={{ color: 'var(--text)' }}>
              {dashboardData.reviews.total}
            </span>
            <span className="text-[9px]" style={{ color: chartColors.warning }}>
              {dashboardData.reviews.pending} pend
            </span>
          </div>
        </div>

        {/* Blog Posts Trend */}
        <div
          className="rounded-xl p-3 border"
          style={{ backgroundColor: 'var(--bg)', borderColor: 'var(--border)' }}
        >
          <h3
            className="text-xs font-semibold mb-2 flex items-center gap-1"
            style={{ color: 'var(--text)' }}
          >
            <FiBookOpen
              className="w-3 h-3"
              style={{ color: chartColors.primary }}
            />{' '}
            Blog Posts
          </h3>
          <div style={{ height: '80px' }}>
            <Line data={blogTrendData} options={getLineOptions} />
          </div>
          <div
            className="flex justify-between mt-1 pt-1 border-t"
            style={{ borderColor: 'var(--border)' }}
          >
            <span className="text-[9px]" style={{ color: 'var(--text)' }}>
              {dashboardData.blog.published}
            </span>
            <span className="text-[9px]" style={{ color: chartColors.warning }}>
              {dashboardData.blog.draft} draft
            </span>
          </div>
        </div>

        {/* Blog Comments Trend */}
        <div
          className="rounded-xl p-3 border"
          style={{ backgroundColor: 'var(--bg)', borderColor: 'var(--border)' }}
        >
          <h3
            className="text-xs font-semibold mb-2 flex items-center gap-1"
            style={{ color: 'var(--text)' }}
          >
            <FiMessageSquare
              className="w-3 h-3"
              style={{ color: chartColors.info }}
            />{' '}
            Comments
          </h3>
          <div style={{ height: '80px' }}>
            <Line data={blogCommentTrendData} options={getLineOptions} />
          </div>
          <div
            className="flex justify-between mt-1 pt-1 border-t"
            style={{ borderColor: 'var(--border)' }}
          >
            <span className="text-[9px]" style={{ color: 'var(--text)' }}>
              {dashboardData.blogComments.total}
            </span>
            <span
              className="text-[9px]"
              style={{ color: chartColors.secondary }}
            >
              {dashboardData.blogComments.totalReactions} reactions
            </span>
          </div>
        </div>
      </div>

      {/* Analytics Dashboard - Detailed Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Custom Hire Analytics */}
        <div
          className="rounded-xl p-4 border"
          style={{ backgroundColor: 'var(--bg)', borderColor: 'var(--border)' }}
        >
          <div className="flex items-center justify-between mb-3">
            <h3
              className="text-sm font-semibold flex items-center gap-1.5"
              style={{ color: 'var(--text)' }}
            >
              <FiTarget
                className="w-4 h-4"
                style={{ color: chartColors.success }}
              />{' '}
              Custom Hire Analytics
            </h3>
            <NavLink
              to="/admin/inquiries/hire"
              className="text-[10px] flex items-center gap-1"
              style={{ color: 'var(--text)' }}
            >
              View All <FiArrowRight className="w-2.5 h-2.5" />
            </NavLink>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-[10px] mb-1" style={{ color: 'var(--text)' }}>
                <FiDollarSign
                  className="w-2.5 h-2.5 inline"
                  style={{ color: chartColors.warning }}
                />{' '}
                Budget Distribution
              </p>
              <div style={{ height: '120px' }}>
                <Doughnut
                  data={budgetChartData}
                  options={{ ...getPieOptions, cutout: '50%' }}
                />
              </div>
            </div>
            <div>
              <p className="text-[10px] mb-1" style={{ color: 'var(--text)' }}>
                <FiActivity
                  className="w-2.5 h-2.5 inline"
                  style={{ color: chartColors.info }}
                />{' '}
                Status Breakdown
              </p>
              <div className="grid grid-cols-2 gap-1 mt-2">
                <div
                  className="text-center p-1 rounded"
                  style={{ backgroundColor: `${chartColors.warning}15` }}
                >
                  <div
                    className="text-sm font-bold"
                    style={{ color: chartColors.warning }}
                  >
                    {dashboardData.hires.pending}
                  </div>
                  <div className="text-[9px]" style={{ color: 'var(--text)' }}>
                    Pending
                  </div>
                </div>
                <div
                  className="text-center p-1 rounded"
                  style={{ backgroundColor: `${chartColors.info}15` }}
                >
                  <div
                    className="text-sm font-bold"
                    style={{ color: chartColors.info }}
                  >
                    {dashboardData.hires.inReview}
                  </div>
                  <div className="text-[9px]" style={{ color: 'var(--text)' }}>
                    Review
                  </div>
                </div>
                <div
                  className="text-center p-1 rounded"
                  style={{ backgroundColor: `${chartColors.success}15` }}
                >
                  <div
                    className="text-sm font-bold"
                    style={{ color: chartColors.success }}
                  >
                    {dashboardData.hires.approved}
                  </div>
                  <div className="text-[9px]" style={{ color: 'var(--text)' }}>
                    Approved
                  </div>
                </div>
                <div
                  className="text-center p-1 rounded"
                  style={{ backgroundColor: `${chartColors.error}15` }}
                >
                  <div
                    className="text-sm font-bold"
                    style={{ color: chartColors.error }}
                  >
                    {dashboardData.hires.declined}
                  </div>
                  <div className="text-[9px]" style={{ color: 'var(--text)' }}>
                    Declined
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            className="mt-3 pt-2 border-t flex justify-between text-[10px]"
            style={{ borderColor: 'var(--border)', color: 'var(--text)' }}
          >
            <span>
              Conversion Rate:{' '}
              <strong
                className="font-bold"
                style={{ color: chartColors.success }}
              >
                {dashboardData.hires.conversionRate}%
              </strong>
            </span>
            <span>
              Completed:{' '}
              <strong
                className="font-bold"
                style={{ color: chartColors.emerald }}
              >
                {dashboardData.hires.completed}
              </strong>
            </span>
          </div>
        </div>

        {/* Package Inquiries Status */}
        <div
          className="rounded-xl p-4 border"
          style={{ backgroundColor: 'var(--bg)', borderColor: 'var(--border)' }}
        >
          <div className="flex items-center justify-between mb-3">
            <h3
              className="text-sm font-semibold flex items-center gap-1.5"
              style={{ color: 'var(--text)' }}
            >
              <FiPackage
                className="w-4 h-4"
                style={{ color: chartColors.cyan }}
              />{' '}
              Package Inquiries Status
            </h3>
            <NavLink
              to="/admin/inquiries/ready-made"
              className="text-[10px] flex items-center gap-1"
              style={{ color: 'var(--text)' }}
            >
              Manage <FiArrowRight className="w-2.5 h-2.5" />
            </NavLink>
          </div>
          <div style={{ height: '140px' }}>
            <Pie data={readyMadeStatusData} options={getPieOptions} />
          </div>
          <div
            className="grid grid-cols-4 gap-1 mt-3 pt-2 border-t text-center"
            style={{ borderColor: 'var(--border)' }}
          >
            <div>
              <p className="text-[9px]" style={{ color: 'var(--text)' }}>
                Total
              </p>
              <p
                className="text-sm font-bold"
                style={{ color: chartColors.purple }}
              >
                {dashboardData.readyMadeInquiries.total}
              </p>
            </div>
            <div>
              <p className="text-[9px]" style={{ color: 'var(--text)' }}>
                Pending
              </p>
              <p
                className="text-sm font-bold"
                style={{ color: chartColors.warning }}
              >
                {dashboardData.readyMadeInquiries.pending}
              </p>
            </div>
            <div>
              <p className="text-[9px]" style={{ color: 'var(--text)' }}>
                Approved
              </p>
              <p
                className="text-sm font-bold"
                style={{ color: chartColors.success }}
              >
                {dashboardData.readyMadeInquiries.approved}
              </p>
            </div>
            <div>
              <p className="text-[9px]" style={{ color: 'var(--text)' }}>
                Declined
              </p>
              <p
                className="text-sm font-bold"
                style={{ color: chartColors.error }}
              >
                {dashboardData.readyMadeInquiries.declined}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Subscription Inquiries Status */}
        <div
          className="rounded-xl p-4 border"
          style={{ backgroundColor: 'var(--bg)', borderColor: 'var(--border)' }}
        >
          <div className="flex items-center justify-between mb-3">
            <h3
              className="text-sm font-semibold flex items-center gap-1.5"
              style={{ color: 'var(--text)' }}
            >
              <FiCalendar
                className="w-4 h-4"
                style={{ color: chartColors.purple }}
              />{' '}
              Subscription Status
            </h3>
            <NavLink
              to="/admin/inquiries/subscription"
              className="text-[10px] flex items-center gap-1"
              style={{ color: 'var(--text)' }}
            >
              Manage <FiArrowRight className="w-2.5 h-2.5" />
            </NavLink>
          </div>
          <div style={{ height: '140px' }}>
            <Pie data={subscriptionStatusData} options={getPieOptions} />
          </div>
          <div
            className="grid grid-cols-3 gap-2 mt-3 pt-2 border-t text-center"
            style={{ borderColor: 'var(--border)' }}
          >
            <div>
              <p className="text-[9px]" style={{ color: 'var(--text)' }}>
                Total
              </p>
              <p
                className="text-sm font-bold"
                style={{ color: chartColors.purple }}
              >
                {dashboardData.subscriptionInquiries.total}
              </p>
            </div>
            <div>
              <p className="text-[9px]" style={{ color: 'var(--text)' }}>
                Monthly
              </p>
              <p
                className="text-sm font-bold"
                style={{ color: chartColors.info }}
              >
                {dashboardData.subscriptionInquiries.monthly}
              </p>
            </div>
            <div>
              <p className="text-[9px]" style={{ color: 'var(--text)' }}>
                Yearly
              </p>
              <p
                className="text-sm font-bold"
                style={{ color: chartColors.success }}
              >
                {dashboardData.subscriptionInquiries.yearly}
              </p>
            </div>
          </div>
        </div>

        {/* Reviews Analytics */}
        <div
          className="rounded-xl p-4 border"
          style={{ backgroundColor: 'var(--bg)', borderColor: 'var(--border)' }}
        >
          <div className="flex items-center justify-between mb-3">
            <h3
              className="text-sm font-semibold flex items-center gap-1.5"
              style={{ color: 'var(--text)' }}
            >
              <FiStar
                className="w-4 h-4"
                style={{ color: chartColors.secondary }}
              />{' '}
              Reviews Analytics
            </h3>
            <NavLink
              to="/admin/reviews"
              className="text-[10px] flex items-center gap-1"
              style={{ color: 'var(--text)' }}
            >
              Manage <FiArrowRight className="w-2.5 h-2.5" />
            </NavLink>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-[10px] mb-1" style={{ color: 'var(--text)' }}>
                <FiStar
                  className="w-2.5 h-2.5 inline"
                  style={{ color: chartColors.warning }}
                />{' '}
                Rating Distribution
              </p>
              <div style={{ height: '120px' }}>
                <Doughnut
                  data={reviewsRatingDistributionData}
                  options={{ ...getPieOptions, cutout: '50%' }}
                />
              </div>
            </div>
            <div>
              <p className="text-[10px] mb-1" style={{ color: 'var(--text)' }}>
                <FiHeart
                  className="w-2.5 h-2.5 inline"
                  style={{ color: chartColors.secondary }}
                />{' '}
                Review Stats
              </p>
              <div className="space-y-2 mt-2">
                <div className="flex justify-between items-center">
                  <span
                    className="text-[10px]"
                    style={{ color: 'var(--text)' }}
                  >
                    Avg Rating
                  </span>
                  <div className="flex items-center gap-1">
                    <StarRating rating={dashboardData.reviews.averageRating} />
                    <span
                      className="text-xs font-bold"
                      style={{ color: chartColors.secondary }}
                    >
                      {dashboardData.reviews.averageRating}
                    </span>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span
                    className="text-[10px]"
                    style={{ color: 'var(--text)' }}
                  >
                    Approved
                  </span>
                  <span
                    className="text-sm font-bold"
                    style={{ color: chartColors.success }}
                  >
                    {dashboardData.reviews.approved}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span
                    className="text-[10px]"
                    style={{ color: 'var(--text)' }}
                  >
                    Pending
                  </span>
                  <span
                    className="text-sm font-bold"
                    style={{ color: chartColors.warning }}
                  >
                    {dashboardData.reviews.pending}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span
                    className="text-[10px]"
                    style={{ color: 'var(--text)' }}
                  >
                    Rejected
                  </span>
                  <span
                    className="text-sm font-bold"
                    style={{ color: chartColors.error }}
                  >
                    {dashboardData.reviews.rejected}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div
            className="mt-3 pt-2 border-t flex justify-between text-[10px]"
            style={{ borderColor: 'var(--border)', color: 'var(--text)' }}
          >
            <span>
              Total Reviews:{' '}
              <strong
                className="font-bold"
                style={{ color: chartColors.secondary }}
              >
                {dashboardData.reviews.total}
              </strong>
            </span>
            <NavLink
              to="/admin/reviews"
              className="flex items-center gap-1 hover:opacity-70"
              style={{ color: chartColors.info }}
            >
              View All Reviews <FiArrowRight className="w-2.5 h-2.5" />
            </NavLink>
          </div>
        </div>
      </div>

      {/* Recent Activity Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Recent Hire Inquiries */}
        <div
          className="rounded-xl p-4 border"
          style={{ backgroundColor: 'var(--bg)', borderColor: 'var(--border)' }}
        >
          <h3
            className="text-sm font-semibold mb-3 flex items-center gap-1.5"
            style={{ color: 'var(--text)' }}
          >
            <FiUsers
              className="w-4 h-4"
              style={{ color: chartColors.success }}
            />{' '}
            Recent Custom Hire Inquiries
          </h3>
          <div className="space-y-2">
            {dashboardData.hires.recentInquiries.length === 0 ? (
              <div className="text-center py-6">
                <FiBriefcase
                  className="w-6 h-6 mx-auto mb-1 opacity-50"
                  style={{ color: 'var(--text)' }}
                />
                <p className="text-xs" style={{ color: 'var(--text)' }}>
                  No hire inquiries yet
                </p>
              </div>
            ) : (
              dashboardData.hires.recentInquiries.map(inquiry => (
                <div
                  key={inquiry.id}
                  className="flex items-center gap-2 p-2 rounded-lg transition-all hover:translate-x-0.5 group"
                  style={{ backgroundColor: 'var(--border)' }}
                >
                  <div
                    className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: 'var(--bg)' }}
                  >
                    <FiUser
                      className="w-3.5 h-3.5"
                      style={{ color: 'var(--text)' }}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p
                      className="text-xs font-medium truncate"
                      style={{ color: 'var(--text)' }}
                    >
                      {inquiry.name}
                    </p>
                    <p
                      className="text-[10px] truncate"
                      style={{ color: 'var(--text)' }}
                    >
                      {inquiry.projectType}
                    </p>
                  </div>
                  <span
                    className={`text-[9px] px-1.5 py-0.5 rounded-full ${
                      inquiry.status === 'pending'
                        ? 'bg-yellow-500/10 text-yellow-500'
                        : inquiry.status === 'approved'
                          ? 'bg-green-500/10 text-green-500'
                          : 'bg-blue-500/10 text-blue-500'
                    }`}
                  >
                    {inquiry.status}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Recent Contact Messages */}
        <div
          className="rounded-xl p-4 border"
          style={{ backgroundColor: 'var(--bg)', borderColor: 'var(--border)' }}
        >
          <h3
            className="text-sm font-semibold mb-3 flex items-center gap-1.5"
            style={{ color: 'var(--text)' }}
          >
            <FiMessageSquare
              className="w-4 h-4"
              style={{ color: chartColors.info }}
            />{' '}
            Recent Contact Messages
          </h3>
          <div className="space-y-2">
            {dashboardData.contacts.recentMessages.length === 0 ? (
              <div className="text-center py-6">
                <FiMail
                  className="w-6 h-6 mx-auto mb-1 opacity-50"
                  style={{ color: 'var(--text)' }}
                />
                <p className="text-xs" style={{ color: 'var(--text)' }}>
                  No contact messages yet
                </p>
              </div>
            ) : (
              dashboardData.contacts.recentMessages.map(message => (
                <div
                  key={message.id}
                  className="flex items-center gap-2 p-2 rounded-lg transition-all hover:translate-x-0.5 group"
                  style={{ backgroundColor: 'var(--border)' }}
                >
                  <div
                    className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: 'var(--bg)' }}
                  >
                    <FiUser
                      className="w-3.5 h-3.5"
                      style={{ color: 'var(--text)' }}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p
                      className="text-xs font-medium truncate"
                      style={{ color: 'var(--text)' }}
                    >
                      {message.name}
                    </p>
                    <p
                      className="text-[10px] truncate"
                      style={{ color: 'var(--text)' }}
                    >
                      {message.message}
                    </p>
                  </div>
                  <span
                    className={`text-[9px] px-1.5 py-0.5 rounded-full ${
                      message.status === 'unread'
                        ? 'bg-blue-500/10 text-blue-500'
                        : 'bg-green-500/10 text-green-500'
                    }`}
                  >
                    {message.status}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Recent Reviews */}
        <div
          className="rounded-xl p-4 border"
          style={{ backgroundColor: 'var(--bg)', borderColor: 'var(--border)' }}
        >
          <h3
            className="text-sm font-semibold mb-3 flex items-center gap-1.5"
            style={{ color: 'var(--text)' }}
          >
            <FiStar
              className="w-4 h-4"
              style={{ color: chartColors.secondary }}
            />{' '}
            Recent Reviews
          </h3>
          <div className="space-y-2">
            {dashboardData.reviews.recentReviews.length === 0 ? (
              <div className="text-center py-6">
                <FiStar
                  className="w-6 h-6 mx-auto mb-1 opacity-50"
                  style={{ color: 'var(--text)' }}
                />
                <p className="text-xs" style={{ color: 'var(--text)' }}>
                  No reviews yet
                </p>
              </div>
            ) : (
              dashboardData.reviews.recentReviews.map((review, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-2 p-2 rounded-lg transition-all hover:translate-x-0.5 group"
                  style={{ backgroundColor: 'var(--border)' }}
                >
                  {review.imageUrl ? (
                    <img
                      src={review.imageUrl}
                      alt={review.name}
                      className="w-7 h-7 rounded-full object-cover"
                    />
                  ) : (
                    <div
                      className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: 'var(--bg)' }}
                    >
                      <FiUser
                        className="w-3.5 h-3.5"
                        style={{ color: 'var(--text)' }}
                      />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p
                        className="text-xs font-medium truncate"
                        style={{ color: 'var(--text)' }}
                      >
                        {review.name}
                      </p>
                      <StarRating rating={review.rating} />
                    </div>
                    <p
                      className="text-[10px] truncate"
                      style={{ color: 'var(--text)' }}
                    >
                      {review.message?.substring(0, 50)}...
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
          <div
            className="mt-3 pt-2 border-t text-center"
            style={{ borderColor: 'var(--border)' }}
          >
            <NavLink
              to="/admin/reviews"
              className="text-[10px] flex items-center justify-center gap-1 hover:opacity-70"
              style={{ color: chartColors.info }}
            >
              Manage All Reviews <FiArrowRight className="w-2.5 h-2.5" />
            </NavLink>
          </div>
        </div>
      </div>

      {/* Recent Blog Activity Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Recent Blog Posts */}
        <div
          className="rounded-xl p-4 border"
          style={{ backgroundColor: 'var(--bg)', borderColor: 'var(--border)' }}
        >
          <h3
            className="text-sm font-semibold mb-3 flex items-center gap-1.5"
            style={{ color: 'var(--text)' }}
          >
            <FiBookOpen
              className="w-4 h-4"
              style={{ color: chartColors.primary }}
            />{' '}
            Recent Blog Posts
          </h3>
          <div className="space-y-2">
            {dashboardData.blog.recentPosts.length === 0 ? (
              <div className="text-center py-6">
                <FiBookOpen
                  className="w-6 h-6 mx-auto mb-1 opacity-50"
                  style={{ color: 'var(--text)' }}
                />
                <p className="text-xs" style={{ color: 'var(--text)' }}>
                  No blog posts yet
                </p>
              </div>
            ) : (
              dashboardData.blog.recentPosts.map(post => (
                <div
                  key={post.id}
                  className="flex items-center gap-2 p-2 rounded-lg transition-all hover:translate-x-0.5 group"
                  style={{ backgroundColor: 'var(--border)' }}
                >
                  <div
                    className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{
                      backgroundColor: `${chartColors.primary}20`,
                      color: chartColors.primary,
                    }}
                  >
                    <FiBookOpen className="w-3.5 h-3.5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p
                      className="text-xs font-medium truncate"
                      style={{ color: 'var(--text)' }}
                    >
                      {post.title}
                    </p>
                    <div className="flex items-center gap-2">
                      <p
                        className="text-[10px]"
                        style={{ color: 'var(--text)' }}
                      >
                        {post.category}
                      </p>
                      <p
                        className="text-[10px]"
                        style={{ color: 'var(--text)' }}
                      >
                        {post.views} views
                      </p>
                    </div>
                  </div>
                  <NavLink
                    to={`/blog/${post.slug}`}
                    target="_blank"
                    className="text-[10px] flex items-center gap-1 hover:opacity-70"
                    style={{ color: chartColors.info }}
                  >
                    View <FiArrowRight className="w-2.5 h-2.5" />
                  </NavLink>
                </div>
              ))
            )}
          </div>
          <div
            className="mt-3 pt-2 border-t text-center"
            style={{ borderColor: 'var(--border)' }}
          >
            <NavLink
              to="/admin/blog"
              className="text-[10px] flex items-center justify-center gap-1 hover:opacity-70"
              style={{ color: chartColors.info }}
            >
              Manage All Blog Posts <FiArrowRight className="w-2.5 h-2.5" />
            </NavLink>
          </div>
        </div>

        {/* Top Performing Blogs */}
        <div
          className="rounded-xl p-4 border"
          style={{ backgroundColor: 'var(--bg)', borderColor: 'var(--border)' }}
        >
          <h3
            className="text-sm font-semibold mb-3 flex items-center gap-1.5"
            style={{ color: 'var(--text)' }}
          >
            <FiTrendingUp
              className="w-4 h-4"
              style={{ color: chartColors.warning }}
            />{' '}
            Top Performing Blogs
          </h3>
          <div className="space-y-2">
            {dashboardData.blog.topPerforming.length === 0 ? (
              <div className="text-center py-6">
                <FiTrendingUp
                  className="w-6 h-6 mx-auto mb-1 opacity-50"
                  style={{ color: 'var(--text)' }}
                />
                <p className="text-xs" style={{ color: 'var(--text)' }}>
                  No blog performance data yet
                </p>
              </div>
            ) : (
              dashboardData.blog.topPerforming.slice(0, 5).map((blog, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between p-2 rounded-lg transition-all hover:translate-x-0.5"
                  style={{ backgroundColor: 'var(--border)' }}
                >
                  <div className="flex-1 min-w-0">
                    <p
                      className="text-xs font-medium truncate"
                      style={{ color: 'var(--text)' }}
                    >
                      #{idx + 1} {blog.title}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 text-[10px]">
                    <span style={{ color: 'var(--text)' }}>
                      {blog.views} views
                    </span>
                    <span
                      className="font-bold"
                      style={{ color: chartColors.info }}
                    >
                      {blog.likes} ❤️
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Services & Plans Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div
          className="rounded-xl p-4 border"
          style={{ backgroundColor: 'var(--bg)', borderColor: 'var(--border)' }}
        >
          <h3
            className="text-sm font-semibold mb-3 flex items-center gap-1.5"
            style={{ color: 'var(--text)' }}
          >
            <FiPackage
              className="w-4 h-4"
              style={{ color: chartColors.primary }}
            />
            Ready-made Services Overview
          </h3>
          <div className="grid grid-cols-3 gap-3">
            <div
              className="text-center p-2 rounded-lg"
              style={{ backgroundColor: 'var(--border)' }}
            >
              <p className="text-[10px]" style={{ color: 'var(--text)' }}>
                Total
              </p>
              <p
                className="text-lg font-bold"
                style={{ color: chartColors.primary }}
              >
                {dashboardData.readyMadeServices.total}
              </p>
            </div>
            <div
              className="text-center p-2 rounded-lg"
              style={{ backgroundColor: 'var(--border)' }}
            >
              <p className="text-[10px]" style={{ color: 'var(--text)' }}>
                Active
              </p>
              <p
                className="text-lg font-bold"
                style={{ color: chartColors.success }}
              >
                {dashboardData.readyMadeServices.active}
              </p>
            </div>
            <div
              className="text-center p-2 rounded-lg"
              style={{ backgroundColor: 'var(--border)' }}
            >
              <p className="text-[10px]" style={{ color: 'var(--text)' }}>
                Popular
              </p>
              <p
                className="text-lg font-bold"
                style={{ color: chartColors.warning }}
              >
                {dashboardData.readyMadeServices.popular}
              </p>
            </div>
          </div>
        </div>

        <div
          className="rounded-xl p-4 border"
          style={{ backgroundColor: 'var(--bg)', borderColor: 'var(--border)' }}
        >
          <h3
            className="text-sm font-semibold mb-3 flex items-center gap-1.5"
            style={{ color: 'var(--text)' }}
          >
            <FiCalendar
              className="w-4 h-4"
              style={{ color: chartColors.purple }}
            />
            Subscription Plans Overview
          </h3>
          <div className="grid grid-cols-3 gap-3">
            <div
              className="text-center p-2 rounded-lg"
              style={{ backgroundColor: 'var(--border)' }}
            >
              <p className="text-[10px]" style={{ color: 'var(--text)' }}>
                Total
              </p>
              <p
                className="text-lg font-bold"
                style={{ color: chartColors.purple }}
              >
                {dashboardData.subscriptionPlans.total}
              </p>
            </div>
            <div
              className="text-center p-2 rounded-lg"
              style={{ backgroundColor: 'var(--border)' }}
            >
              <p className="text-[10px]" style={{ color: 'var(--text)' }}>
                Active
              </p>
              <p
                className="text-lg font-bold"
                style={{ color: chartColors.success }}
              >
                {dashboardData.subscriptionPlans.active}
              </p>
            </div>
            <div
              className="text-center p-2 rounded-lg"
              style={{ backgroundColor: 'var(--border)' }}
            >
              <p className="text-[10px]" style={{ color: 'var(--text)' }}>
                Popular
              </p>
              <p
                className="text-lg font-bold"
                style={{ color: chartColors.warning }}
              >
                {dashboardData.subscriptionPlans.popular}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div
        className="rounded-xl p-4 border"
        style={{ backgroundColor: 'var(--bg)', borderColor: 'var(--border)' }}
      >
        <h2
          className="text-sm font-semibold mb-3 flex items-center gap-1.5"
          style={{ color: 'var(--text)' }}
        >
          <FiZap className="w-4 h-4" style={{ color: chartColors.warning }} />{' '}
          Quick Actions
        </h2>
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-10 gap-2">
          <NavLink
            to="/admin/portfolio"
            className="flex items-center justify-center gap-1.5 p-2 rounded-lg transition-all hover:scale-105 text-xs"
            style={{ backgroundColor: 'var(--border)', color: 'var(--text)' }}
          >
            <FiPlus className="w-3 h-3" /> Add Project
          </NavLink>
          <NavLink
            to="/admin/blog"
            className="flex items-center justify-center gap-1.5 p-2 rounded-lg transition-all hover:scale-105 text-xs"
            style={{ backgroundColor: 'var(--border)', color: 'var(--text)' }}
          >
            <FiPlus className="w-3 h-3" /> New Blog
          </NavLink>
          <NavLink
            to="/admin/ready-made-services"
            className="flex items-center justify-center gap-1.5 p-2 rounded-lg transition-all hover:scale-105 text-xs"
            style={{ backgroundColor: 'var(--border)', color: 'var(--text)' }}
          >
            <FiPackage className="w-3 h-3" /> Services
          </NavLink>
          <NavLink
            to="/admin/subscription-plans"
            className="flex items-center justify-center gap-1.5 p-2 rounded-lg transition-all hover:scale-105 text-xs"
            style={{ backgroundColor: 'var(--border)', color: 'var(--text)' }}
          >
            <FiCalendar className="w-3 h-3" /> Plans
          </NavLink>
          <NavLink
            to="/admin/inquiries/hire"
            className="flex items-center justify-center gap-1.5 p-2 rounded-lg transition-all hover:scale-105 text-xs"
            style={{ backgroundColor: 'var(--border)', color: 'var(--text)' }}
          >
            <FiBriefcase className="w-3 h-3" /> Hire
          </NavLink>
          <NavLink
            to="/admin/inquiries/ready-made"
            className="flex items-center justify-center gap-1.5 p-2 rounded-lg transition-all hover:scale-105 text-xs"
            style={{ backgroundColor: 'var(--border)', color: 'var(--text)' }}
          >
            <FiShoppingBag className="w-3 h-3" /> Packages
          </NavLink>
          <NavLink
            to="/admin/inquiries/subscription"
            className="flex items-center justify-center gap-1.5 p-2 rounded-lg transition-all hover:scale-105 text-xs"
            style={{ backgroundColor: 'var(--border)', color: 'var(--text)' }}
          >
            <FiUsers className="w-3 h-3" /> Subscriptions
          </NavLink>
          <NavLink
            to="/admin/reviews"
            className="flex items-center justify-center gap-1.5 p-2 rounded-lg transition-all hover:scale-105 text-xs"
            style={{ backgroundColor: 'var(--border)', color: 'var(--text)' }}
          >
            <FiStar className="w-3 h-3" /> Reviews
          </NavLink>
          <NavLink
            to="/admin/inquiries/contact"
            className="flex items-center justify-center gap-1.5 p-2 rounded-lg transition-all hover:scale-105 text-xs"
            style={{ backgroundColor: 'var(--border)', color: 'var(--text)' }}
          >
            <FiMail className="w-3 h-3" /> Contacts
          </NavLink>
          <NavLink
            to="/admin/settings"
            className="flex items-center justify-center gap-1.5 p-2 rounded-lg transition-all hover:scale-105 text-xs"
            style={{ backgroundColor: 'var(--border)', color: 'var(--text)' }}
          >
            <FiSettings className="w-3 h-3" /> Settings
          </NavLink>
        </div>
      </div>

      {/* Footer Message */}
      <div
        className="rounded-lg p-2.5 text-center"
        style={{ backgroundColor: 'var(--border)' }}
      >
        <p className="text-[10px]" style={{ color: 'var(--text)' }}>
          🚀 Your business is performing well!
          {dashboardData.contacts.unread > 0 &&
            ` You have ${dashboardData.contacts.unread} unread messages.`}
          {dashboardData.readyMadeInquiries.pending > 0 &&
            ` ${dashboardData.readyMadeInquiries.pending} package inquiries need attention.`}
          {dashboardData.subscriptionInquiries.pending > 0 &&
            ` ${dashboardData.subscriptionInquiries.pending} subscription requests pending.`}
          {dashboardData.reviews.pending > 0 &&
            ` ${dashboardData.reviews.pending} reviews awaiting approval.`}
          {dashboardData.blog.draft > 0 &&
            ` ${dashboardData.blog.draft} blog drafts ready to publish.`}
          {dashboardData.blogComments.total > 0 &&
            ` ${dashboardData.blogComments.total} blog comments to review.`}
        </p>
      </div>
    </div>
  );
}

export default DashboardHome;
